import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import { timeOffEndpoints } from '@/lib/api/endpoints';
import { timeOffKeys } from '@/lib/queryKeys';
import type { CreateTimeOffDto, TimeOffRequest } from '@/types/timeOff';

export function useTimeOffRequests(technicianId: string | undefined) {
  return useApiQuery<TimeOffRequest[]>({
    queryKey: timeOffKeys.list(technicianId ?? ''),
    queryFn: () =>
      apiGet<TimeOffRequest[]>(operationsClient, timeOffEndpoints.list(technicianId ?? '')),
    enabled: Boolean(technicianId),
  });
}

export function useCreateTimeOff(technicianId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<TimeOffRequest, CreateTimeOffDto>({
    mutationFn: (dto) =>
      apiPost<TimeOffRequest, CreateTimeOffDto>(
        operationsClient,
        timeOffEndpoints.create(technicianId),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: timeOffKeys.list(technicianId),
      });
      toast.success('Time-off request submitted');
    },
  });
}

export function useApproveTimeOff(technicianId: string, onApproved?: () => void) {
  const queryClient = useQueryClient();
  return useApiMutation<TimeOffRequest, string>({
    mutationFn: (id) =>
      apiPost<TimeOffRequest, undefined>(operationsClient, timeOffEndpoints.approve(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: timeOffKeys.list(technicianId),
      });
      onApproved?.();
      toast.success('Time off approved');
    },
  });
}

export function useDeclineTimeOff(technicianId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<TimeOffRequest, string>({
    mutationFn: (id) =>
      apiPost<TimeOffRequest, undefined>(operationsClient, timeOffEndpoints.decline(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: timeOffKeys.list(technicianId),
      });
      toast.success('Time off declined');
    },
  });
}

/**
 * Cancel a pending/approved request. The backend deletes the linked TIME_OFF
 * blocked time, so `onCancelled` should refresh the technician detail — the
 * schedule frees up immediately.
 */
export function useCancelTimeOff(technicianId: string, onCancelled?: () => void) {
  const queryClient = useQueryClient();
  return useApiMutation<TimeOffRequest, string>({
    mutationFn: (id) =>
      apiPost<TimeOffRequest, undefined>(operationsClient, timeOffEndpoints.cancel(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: timeOffKeys.list(technicianId),
      });
      onCancelled?.();
      toast.success('Time off cancelled — the days are bookable again');
    },
  });
}
